import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { createAnalyticsService } from "@/services/admin/analytics.service";
import type { RegistrationCountByDay } from "@/services/admin/analytics.interfaces";
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, subDays } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";

const dateRangeSchema = z.object({
  from: z.string().min(1, "Data inicial é obrigatória"),
  to: z.string().min(1, "Data final é obrigatória"),
}).refine(
  (data) => {
    const from = new Date(data.from);
    const to = new Date(data.to);
    return from <= to;
  },
  { message: "Data inicial deve ser menor ou igual à data final", path: ["to"] }
);

type DateRangeForm = z.infer<typeof dateRangeSchema>;

const defaultFrom = format(subDays(new Date(), 30), "yyyy-MM-dd");
const defaultTo = format(new Date(), "yyyy-MM-dd");

const analyticsService = createAnalyticsService();

const RegistrationsByDayPage: React.FC = () => {
  const [data, setData] = useState<RegistrationCountByDay[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit } = useForm<DateRangeForm>({
    resolver: zodResolver(dateRangeSchema),
    defaultValues: { from: defaultFrom, to: defaultTo },
  });

  const fetchData = useCallback(async (from: string, to: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await analyticsService.getRegistrationsByDay(from, to);
      setData(result);
    } catch (e) {
      const message =
        axios.isAxiosError(e) && e.response?.status === 503
          ? "Analytics temporarily unavailable."
          : e instanceof Error ? e.message : "Erro ao carregar dados";
      setError(message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(defaultFrom, defaultTo);
  }, [fetchData]);

  const onSubmit = (values: DateRangeForm) => {
    fetchData(values.from, values.to);
  };

  return (
    <AdminPageLayout title="Cadastros por dia">
      <div className="container mx-auto px-4 py-6 space-y-6">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-wrap items-end gap-4 p-4 bg-white rounded-lg border shadow-sm"
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="from">De</Label>
            <Input
              id="from"
              type="date"
              {...register("from")}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="to">Até</Label>
            <Input
              id="to"
              type="date"
              {...register("to")}
            />
          </div>
          <Button type="submit" disabled={loading}>
            {loading ? "Carregando..." : "Aplicar"}
          </Button>
        </form>

        {error && (
          <div className="p-4 bg-red-50 text-red-700 rounded-lg border border-red-200">
            {error}
          </div>
        )}

        <div className="p-4 bg-white rounded-lg border shadow-sm min-h-[400px]">
          <h3 className="text-lg font-semibold mb-4">Cadastros por dia</h3>
          {loading ? (
            <div className="flex items-center justify-center h-64 text-neutral-500">
              Carregando...
            </div>
          ) : data.length === 0 && !error ? (
            <div className="flex items-center justify-center h-64 text-neutral-500">
              Nenhum dado no período selecionado.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={360}>
              <BarChart
                data={data}
                margin={{ top: 16, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="hsl(var(--primary))" name="Cadastros" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </AdminPageLayout>
  );
};

export default RegistrationsByDayPage;
